import './index.css';

interface VideoItem {
  url: string;
  title: string;
}

interface VoteItem {
  videoTitle: string;
  votes: number;
}

interface User {
  username: string;
}

interface VotingSectionProps {
  user: User;
  host: string;
  activeVote: VoteItem[] | null;
  activeReorderVote: VoteItem[] | null;
  startVote: () => void;
  voteForVideoRemove: (title: string) => void;
  concludeVote: () => void;
  concludeWithNoWinner: () => void;
  startReorderQueue: () => void;
  voteForReorder: (title: string) => void;
  concludeReorderQueue: () => void;
  queue: VideoItem[];
  className?: string;
}

const VotingSection = ({
  user,
  host,
  activeVote,
  activeReorderVote,
  startVote,
  voteForVideoRemove,
  concludeVote,
  concludeWithNoWinner,
  startReorderQueue,
  voteForReorder,
  concludeReorderQueue,
  queue,
  className = '',
}: VotingSectionProps) => {
  const isHost = user.username === host;
  const hasActiveVote = activeVote !== null || activeReorderVote !== null;

  return (
    <div className={`voting-container ${className}`}>
      <div className='voting-header'>
        <h2 className='voting-title'>
          <i className='material-icons text-xl'>how_to_vote</i> Voting Controls
        </h2>
        {!isHost && (
          <div className='host-restriction'>
            <i className='material-icons text-xl'>lock</i> Only the host can manage votes
          </div>
        )}
      </div>

      {isHost && (
        <div className='vote-initiation'>
          <button
            type='button'
            onClick={startVote}
            className={`vote-button remove ${activeVote ? 'active' : ''}`}
            disabled={hasActiveVote}
            title='Start vote to remove a video'>
            Remove Video
          </button>

          <button
            type='button'
            onClick={startReorderQueue}
            className={`vote-button reorder ${activeReorderVote ? 'active' : ''}`}
            disabled={hasActiveVote}
            title='Start vote to reorder queue'>
            Reorder Queue
          </button>
        </div>
      )}

      {(activeVote || activeReorderVote) && (
        <div className='vote-options-container'>
          <h3 className='vote-options-title'>
            {activeVote ? 'Vote to Remove' : 'Vote to Reorder'}
          </h3>

          <div className='vote-options'>
            {(activeVote || activeReorderVote)?.map(video => (
              <div key={video.videoTitle} className='vote-option'>
                <div className='video-info'>
                  <span className='video-vote-title'>{video.videoTitle}</span>
                  <span className='vote-count'>{video.votes} votes</span>
                </div>
                <button
                  type='button'
                  onClick={() =>
                    activeVote
                      ? voteForVideoRemove(video.videoTitle)
                      : voteForReorder(video.videoTitle)
                  }
                  className='vote-button'>
                  Vote
                </button>
              </div>
            ))}
          </div>

          {isHost && (
            <div className='vote-conclusion'>
              {activeVote && (
                <>
                  <button type='button' onClick={concludeVote} className='conclude-button success'>
                    <i className='material-icons text-xl'>check</i> Finalize Removal
                  </button>
                  <button
                    type='button'
                    onClick={concludeWithNoWinner}
                    className='conclude-button cancel'>
                    <i className='material-icons text-xl'>cancel</i> Cancel Removal
                  </button>
                </>
              )}

              {activeReorderVote && (
                <button
                  type='button'
                  onClick={concludeReorderQueue}
                  className='conclude-button success'>
                  <i className='material-icons text-xl'>check</i> Finalize Reorder
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {hasActiveVote && (
        <div className='current-queue'>
          <h3 className='queue-title'>Current Queue</h3>
          <ul className='queue-list'>
            {queue.map((video, index) => (
              <li key={video.url} className='queue-item'>
                <span className='queue-position'>{index + 1}.</span>
                <span className='queue-video-title'>{video.title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VotingSection;
